import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirestore, collection, query, where, getDocs, limit, doc, getDoc } from "firebase/firestore";
import app from "../../firebase";
import { clearEnrolledSession } from "../../auth/roleSession";
import Flowchart from "../academics/Flowchart";

export default function CurriculumOverview({ student: propStudent }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [student, setStudent] = useState(propStudent || location.state?.student || null);
  const [resolvedRef, setResolvedRef] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [detectionError, setDetectionError] = useState("");

  const SIDEBAR_WIDTH = 240;
  const HEADER_HEIGHT = 70;
  const PAGE_PADDING = 32;
  const CONTENT_MAX = 1100;
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // Try to load from sessionStorage if no student provided
  let enrolledStudentFromStorage = null;
  if (!propStudent && !location.state?.student) {
    try {
      const stored = localStorage.getItem("enrolledStudentData");
      if (stored) {
        enrolledStudentFromStorage = JSON.parse(stored);
      }
    } catch (e) {
      console.error("Failed to parse enrolledStudentData from sessionStorage:", e);
    }
  }

  const resolvedStudentId = propStudent?.id || location.state?.student?.id || enrolledStudentFromStorage?.id;

  // Resolve student from Firebase
  useEffect(() => {
    if (!resolvedStudentId) {
      setDetectionError("No student ID available");
      setDetecting(false);
      return;
    }

    if (student) return;

    setDetecting(true);
    setDetectionError("");

    const db = getFirestore(app);

    (async () => {
      try {
        const directRef = doc(db, "students", String(resolvedStudentId).trim());
        const directSnap = await getDoc(directRef);
        let docRef = null;

        if (directSnap.exists()) {
          docRef = directRef;
        } else {
          const q1 = query(collection(db, "students"), where("studentId", "==", String(resolvedStudentId).trim()), limit(1));
          const qSnap1 = await getDocs(q1);
          if (!qSnap1.empty) {
            docRef = doc(db, "students", qSnap1.docs[0].id);
          } else {
            const q2 = query(collection(db, "students"), where("idNumber", "==", String(resolvedStudentId).trim()), limit(1));
            const qSnap2 = await getDocs(q2);
            if (!qSnap2.empty) {
              docRef = doc(db, "students", qSnap2.docs[0].id);
            }
          }
        }

        if (!docRef) {
          setDetectionError("Student record not found.");
          setDetecting(false);
          return;
        }

        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setStudent({ id: docSnap.id, ...docSnap.data() });
          setResolvedRef(docRef);
        } else {
          setDetectionError("Student record not found.");
        }
      } catch (err) {
        console.error("CurriculumOverview student resolution error:", err);
        setDetectionError("Failed to load student record.");
      } finally {
        setDetecting(false);
      }
    })();
  }, [resolvedStudentId, student]);

  const dept = (student?.department || student?.program || "").trim();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [debugInfo, setDebugInfo] = useState({ fetchedCount: 0, distinctDepts: [], samples: [] });
  const [scrollOffset, setScrollOffset] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);

  // Fetch subjects based on student department
  useEffect(() => {
    if (!dept) {
      setSubjects([]);
      setLoading(false);
      setError("Student department not available.");
      return;
    }
    setLoading(true);
    setError("");
    const db = getFirestore(app);
    
    const q = query(
      collection(db, "curriculum_subjects"),
      where("department", "==", dept)
    );
    getDocs(q)
      .then(async (snap) => {
        let items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (items.length === 0) {
          const snapAll = await getDocs(collection(db, "curriculum_subjects"));
          items = snapAll.docs.map((d) => ({ id: d.id, ...d.data() }));
        }

        const distinctDeptsAll = [...new Set(items.map(i => ((i.department || i.addedByDepartment || '')).trim()).filter(Boolean))];
        setDebugInfo({
          fetchedCount: items.length,
          distinctDepts: distinctDeptsAll,
          samples: items.slice(0, 8).map(i => ({ 
            id: i.id, 
            department: i.department || i.addedByDepartment || '', 
            courseId: i.courseId || '', 
            courseName: i.courseName || '' 
          }))
        });

        const deptNormalized = (dept || '').toLowerCase().trim();
        const filtered = deptNormalized
          ? items.filter(i => ((i.department || i.addedByDepartment || '')).toLowerCase().includes(deptNormalized))
          : items;

        // Debug: log student data
        console.log('Student data:', {
          schedules: student?.schedules,
          completedCourses: student?.completedCourses
        });

        // Add color property to each subject based on status
        const coloredSubjects = filtered.map(subject => {
          const enrolledIds = new Set((student?.schedules || []).map(c => c.courseId));
          const completedIds = new Set((student?.completedCourses || []).map(c => c.courseId));
          
          let status = 'notTaken';
          if (completedIds.has(subject.courseId)) status = 'completed';
          else if (enrolledIds.has(subject.courseId)) status = 'enrolled';
          
          const colorMap = {
            'notTaken': '#0f5f3b',
            'enrolled': '#ff6f2e',
            'completed': '#666'
          };
          
          const result = {
            ...subject,
            statusColor: colorMap[status],
            textColor: '#ffffff'
          };
          
          return result;
        });

        console.log('Colored subjects:', coloredSubjects.slice(0, 3));
        setSubjects(coloredSubjects);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load curriculum_subjects:", err);
        setError(err?.message || "Failed to load curriculum.");
        setSubjects([]);
        setLoading(false);
      });
  }, [dept, student]);

  const fullName = student ? `${student.firstName || ""} ${student.lastName || ""}`.trim() : "";
  const profilePicUrl = student?.profilePicUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName || "Enrolled")}`;

  const handleProfile = (action) => {
    setProfileMenuOpen(false);
    if (action === "logout") {
      clearEnrolledSession();
      navigate("/enrolled/signin");
      return;
    }
  };

  const navItems = [
    { key: "home", label: "Home", path: "/enrolled/dashboard" },
    { key: "pre", label: "Pre-Registration", path: "/enrolled/pre-registration" },
    { key: "curriculum", label: "Curriculum Overview", path: "/enrolled/curriculum-overview" },
    { key: "schedule", label: "Class Schedule", path: "/enrolled/class-schedule" },
    { key: "final", label: "COR", path: "/enrolled/cor" },
  ];

  // Handle scroll for hide-on-scroll behavior
  useEffect(() => {
    const mainElement = document.querySelector('[data-scroll-container]');
    if (!mainElement) return;

    const handleScroll = () => {
      const scrollTop = mainElement.scrollTop;
      if (scrollTop > lastScrollTop) {
        setScrollOffset(Math.min(scrollTop - lastScrollTop, HEADER_HEIGHT + SIDEBAR_WIDTH));
      } else {
        setScrollOffset(Math.max(scrollTop - lastScrollTop, -(HEADER_HEIGHT + SIDEBAR_WIDTH)));
      }
      setLastScrollTop(scrollTop);
    };

    mainElement.addEventListener('scroll', handleScroll);
    return () => mainElement.removeEventListener('scroll', handleScroll);
  }, [lastScrollTop]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "Inter, Arial, sans-serif", background: "#efefef" }}>
      {/* Sidebar */}
      <aside style={{ width: SIDEBAR_WIDTH, background: "#0f5f3b", color: "#fff", display: "flex", flexDirection: "column", position: "fixed", height: "100vh", left: 0, top: 0, transform: `translateY(${Math.max(-scrollOffset, -HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 1000 }}>
        <div style={{ padding: "28px 24px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: 1 }}>EnrollEase</div>
        </div>
        <nav style={{ paddingTop: 24, flex: 1 }}>
          {navItems.map((n) => {
            const isActive = window.location.pathname.startsWith(n.path);
            return (
              <div 
                key={n.key} 
                onClick={() => navigate(n.path, { state: { student } })} 
                style={{ 
                  padding: "14px 24px", 
                  margin: "4px 16px", 
                  borderRadius: 8, 
                  cursor: "pointer", 
                  background: isActive ? "#ff6f2e" : "transparent", 
                  color: "#fff", 
                  fontWeight: 600,
                  fontSize: 15,
                  transition: "all 0.2s",
                  boxShadow: isActive ? "0 2px 8px rgba(255,111,46,0.3)" : "none"
                }}
              >
                {n.label}
              </div>
            );
          })}
        </nav>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", marginLeft: SIDEBAR_WIDTH }}>
        {/* Header */}
        <header style={{ height: HEADER_HEIGHT, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${PAGE_PADDING}px`, boxSizing: "border-box", borderBottom: "1px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", position: "sticky", top: 0, transform: `translateY(${-Math.min(scrollOffset, HEADER_HEIGHT)}px)`, transition: "transform 0.3s ease", zIndex: 999, position: "relative" }}>
          <div style={{ color: "#0f5f3b", fontWeight: 700, fontSize: 20 }}>Curriculum Overview</div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: "#0f5f3b", fontWeight: 600, fontSize: 15 }}>{fullName}</div>
            </div>
            <img
              src={profilePicUrl}
              alt="Profile"
              style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", cursor: "pointer", border: "3px solid #ff6f2e" }}
              onClick={() => setProfileMenuOpen(v => !v)}
            />
            {profileMenuOpen && (
              <div style={{ position: "absolute", top: HEADER_HEIGHT - 4, right: PAGE_PADDING, background: "#fff", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.13)", minWidth: 180, padding: "10px 0", zIndex: 100 }}>
                <div style={{ padding: "10px 24px", cursor: "pointer", color: "#ff6f2e", fontWeight: 600 }} onClick={() => handleProfile("logout")}>Log out</div>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main style={{ flex: 1, padding: PAGE_PADDING, boxSizing: "border-box", overflowY: "auto", marginTop: 0 }} data-scroll-container>
          <div style={{ maxWidth: CONTENT_MAX, margin: "0 auto", height: "100%" }}>
            {!student ? (
              detecting ? (
                <div style={{ padding: 18, background: "#fff", borderRadius: 8, border: "1px solid #eee" }}>
                  <div style={{ color: "#0f5f3b", fontWeight: 700 }}>Loading student record…</div>
                  <div style={{ marginTop: 8, color: "#666" }}>Please wait a moment.</div>
                </div>
              ) : detectionError ? (
                <div style={{ padding: 18, background: "#fff", borderRadius: 8, border: "1px solid #eee" }}>
                  <div style={{ color: "#b71c1c", fontWeight: 700 }}>{detectionError}</div>
                  <div style={{ marginTop: 8, color: "#666" }}>Please ensure you are logged in and your student record exists.</div>
                </div>
              ) : (
                <div style={{ padding: 18, background: "#fff", borderRadius: 8, border: "1px solid #eee" }}>
                  <div style={{ color: "#b71c1c", fontWeight: 700 }}>Student information not provided.</div>
                  <div style={{ marginTop: 8, color: "#666" }}>This page needs student data to load the curriculum. Open it from the enrolled dashboard or ensure you are logged in.</div>
                </div>
              )
            ) : (
              <div>
                {/* Flowchart */}
                <div style={{ background: "#fff", padding: 18, borderRadius: 8, border: "1px solid #eee", minHeight: 220 }}>
                  {loading ? (
                    <div style={{ color: "#666" }}>Loading curriculum...</div>
                  ) : error ? (
                    <div style={{ color: "red" }}>{error}</div>
                  ) : subjects.length === 0 ? (
                    <div style={{ color: "#666" }}>
                      <div>No subjects found for department "{dept}".</div>
                      <div style={{ marginTop: 12, fontSize: 13, color: "#444" }}>
                        Debug info:
                        <div>Fetched subjects: {debugInfo.fetchedCount}</div>
                        <div>Distinct departments: {debugInfo.distinctDepts.length ? debugInfo.distinctDepts.join(", ") : "(none)"}</div>
                      </div>
                    </div>
                  ) : (
                    <Flowchart 
                      subjects={subjects} 
                      department={dept} 
                      yearLabel={student.program || student.department || ""} 
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}